import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  Copy,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  StickyNote,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { REPLY_TARGETS, useSalesbots } from '@/features/inbox/api';
import type { WhatsappAccount } from './types';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useAssignConversation,
  useConversation,
  useConversations,
  useSaveWhatsappAccount,
  useSaveWhatsappTemplate,
  useSendWhatsapp,
  useSyncWhatsappTemplates,
  useWhatsappAccounts,
  useWhatsappTemplates,
} from './api';

const TABS = [
  ['inbox', 'Inbox'],
  ['templates', 'Templates'],
  ['numbers', 'Numbers'],
] as const;

export function WhatsappPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'inbox';
  const { data: accounts = [] } = useWhatsappAccounts();

  return (
    <div>
      <PageHeader
        title="WhatsApp"
        description="A shared inbox and approved-template broadcasts, through Meta's Cloud API."
      />

      {accounts.length === 0 && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            No WhatsApp number is connected, so nothing can be sent or received yet. Add one under
            Numbers with the phone number ID, WABA ID and access token from your Meta app, then
            point Meta&rsquo;s webhook at the URL shown there.
          </span>
        </div>
      )}

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'inbox' && <Inbox />}
      {tab === 'templates' && <Templates />}
      {tab === 'numbers' && <Numbers />}
    </div>
  );
}

function Inbox() {
  const [selected, setSelected] = useState<string | null>(null);
  const { data: conversations = [], isLoading } = useConversations();

  if (isLoading) return <TableSkeleton columns={2} />;

  if (conversations.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center">
          <MessageSquare className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No conversations yet. They appear here when someone messages your number.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[20rem_1fr]">
      <Card className="h-fit">
        <CardContent className="divide-y p-0">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => setSelected(conversation.id)}
              className={cn(
                'flex w-full items-start gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/50',
                selected === conversation.id && 'bg-secondary',
              )}
            >
              <Avatar
                name={conversation.displayName ?? conversation.phone}
                className="h-8 w-8 shrink-0 text-[11px]"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {conversation.contact
                      ? `${conversation.contact.firstName} ${conversation.contact.lastName ?? ''}`
                      : (conversation.displayName ?? conversation.phone)}
                  </span>
                  {conversation.unreadCount > 0 && (
                    <span className="tabular ms-auto shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-medium text-primary-foreground">
                      {conversation.unreadCount}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">{conversation.phone}</div>
                <div
                  className={cn(
                    'mt-0.5 flex items-center gap-1 text-[11px]',
                    conversation.sessionOpen ? 'text-emerald-600' : 'text-muted-foreground',
                  )}
                >
                  <Clock className="h-3 w-3" />
                  {conversation.sessionOpen
                    ? `${Math.round(conversation.sessionMinutesLeft / 60)}h left to reply freely`
                    : 'window closed — template only'}
                </div>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      {selected ? (
        <Conversation id={selected} />
      ) : (
        <Card>
          <CardContent className="py-20 text-center text-sm text-muted-foreground">
            Choose a conversation.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Conversation({ id }: { id: string }) {
  const { data: conversation, isLoading } = useConversation(id);
  const send = useSendWhatsapp(id);
  const assign = useAssignConversation(id);
  const { data: users = [] } = useUserOptions();
  const { data: templates = [] } = useWhatsappTemplates();

  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading || !conversation) return <TableSkeleton columns={2} />;

  const approved = templates.filter((template) => template.status === 'APPROVED');
  const canFreeText = conversation.sessionOpen || isNote;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex-row items-start justify-between border-b pb-3">
        <div>
          <CardTitle className="text-base">
            {conversation.contact
              ? `${conversation.contact.firstName} ${conversation.contact.lastName ?? ''}`
              : (conversation.displayName ?? conversation.phone)}
          </CardTitle>
          <CardDescription>
            {conversation.phone}
            {conversation.lead && ` · lead ${conversation.lead.number}`}
            {` · via ${conversation.account.label}`}
          </CardDescription>
        </div>
        <Select
          className="h-8 w-auto text-xs"
          value={conversation.assignedTo?.id ?? ''}
          onChange={(event) => assign.mutate(event.target.value || null)}
        >
          <option value="">Unassigned</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </Select>
      </CardHeader>

      <CardContent className="max-h-[26rem] flex-1 space-y-3 overflow-y-auto py-4">
        {(conversation.messages ?? []).map((message) => (
          <div
            key={message.id}
            className={cn(
              'max-w-[80%] rounded-lg px-3 py-2 text-sm',
              message.isInternalNote
                ? 'ms-auto border border-amber-200 bg-amber-50'
                : message.direction === 'OUTBOUND'
                  ? 'ms-auto bg-primary/10'
                  : 'bg-muted',
            )}
          >
            {message.isInternalNote && (
              <div className="mb-1 flex items-center gap-1 text-[10px] font-medium text-amber-700">
                <StickyNote className="h-3 w-3" />
                internal note
              </div>
            )}
            {message.template && (
              <div className="mb-1 text-[10px] text-muted-foreground">
                template: {message.template.name}
              </div>
            )}
            <p className="whitespace-pre-line">{message.body}</p>
            <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>{formatDateTime(message.createdAt)}</span>
              {message.direction === 'OUTBOUND' && <span>{message.status.toLowerCase()}</span>}
              {message.errorMessage && (
                <span className="text-destructive">{message.errorMessage}</span>
              )}
            </div>
          </div>
        ))}
      </CardContent>

      <div className="space-y-2 border-t p-4">
        {!conversation.sessionOpen && !isNote && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              The 24-hour window has closed. Meta only accepts an approved template now — free
              text would be rejected, so it is disabled rather than sent and bounced.
            </span>
          </div>
        )}

        <div className="flex gap-1 rounded-lg border p-0.5 w-fit">
          <button
            onClick={() => setIsNote(false)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              !isNote ? 'bg-secondary font-medium' : 'text-muted-foreground',
            )}
          >
            Reply
          </button>
          <button
            onClick={() => setIsNote(true)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              isNote ? 'bg-amber-100 font-medium text-amber-900' : 'text-muted-foreground',
            )}
          >
            Internal note
          </button>
        </div>

        {!conversation.sessionOpen && !isNote && approved.length > 0 && (
          <Select
            className="h-8 w-auto text-xs"
            value={templateId}
            onChange={(event) => {
              setTemplateId(event.target.value);
              const template = approved.find((item) => item.id === event.target.value);
              if (template) setBody(template.bodyText);
            }}
          >
            <option value="">Choose an approved template…</option>
            {approved.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.language})
              </option>
            ))}
          </Select>
        )}

        <Textarea
          rows={3}
          disabled={!canFreeText && !templateId}
          placeholder={
            isNote
              ? 'A note only the team can see…'
              : canFreeText
                ? 'Your reply…'
                : 'Choose an approved template above'
          }
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />

        <FormError message={error} />

        <Can permission="marketing.campaign.send">
          <Button
            size="sm"
            className="ms-auto flex"
            disabled={send.isPending || (!body.trim() && !templateId)}
            onClick={async () => {
              setError(null);
              try {
                await send.mutateAsync({
                  body: body || undefined,
                  templateId: !isNote && !conversation.sessionOpen ? templateId || undefined : undefined,
                  isInternalNote: isNote,
                });
                setBody('');
                setTemplateId('');
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not send that.',
                );
              }
            }}
          >
            <Send className="h-3.5 w-3.5" />
            {isNote ? 'Add note' : 'Send'}
          </Button>
        </Can>
      </div>
    </Card>
  );
}

function Templates() {
  const { data: accounts = [] } = useWhatsappAccounts();
  const { data: templates = [], isLoading } = useWhatsappTemplates();
  const sync = useSyncWhatsappTemplates();
  const [adding, setAdding] = useState(false);

  if (isLoading) return <TableSkeleton columns={3} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {accounts.length > 0 && (
          <Can permission="marketing.template.update">
            <Button
              size="sm"
              variant="outline"
              disabled={sync.isPending}
              onClick={() => sync.mutate(accounts[0].id)}
            >
              <RefreshCw className="h-4 w-4" />
              Sync from Meta
            </Button>
          </Can>
        )}
        <Can permission="marketing.template.create">
          <Button size="sm" variant="outline" disabled={accounts.length === 0} onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Draft a template
          </Button>
        </Can>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-muted-foreground">
            No templates. Meta has to approve each one before it can be sent outside a
            conversation window.
          </CardContent>
        </Card>
      ) : (
        templates.map((template) => (
          <Card key={template.id}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-medium">{template.name}</code>
                    <Badge
                      variant={
                        template.status === 'APPROVED'
                          ? 'success'
                          : template.status === 'REJECTED'
                            ? 'destructive'
                            : 'secondary'
                      }
                    >
                      {template.status.toLowerCase()}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {template.category.toLowerCase()}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{template.language}</span>
                  </div>
                  <p className="mt-1.5 whitespace-pre-line text-sm text-muted-foreground">
                    {template.bodyText}
                  </p>
                  {template.rejectionReason && (
                    <p className="mt-1 text-xs text-destructive">{template.rejectionReason}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {adding && <TemplateDialog accountId={accounts[0]?.id} onClose={() => setAdding(false)} />}
    </div>
  );
}

function TemplateDialog({ accountId, onClose }: { accountId?: string; onClose: () => void }) {
  const save = useSaveWhatsappTemplate();
  const [form, setForm] = useState({
    name: '',
    language: 'en',
    category: 'MARKETING',
    bodyText: '',
  });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Draft a template</DialogTitle>
          <DialogDescription>
            Saving it here records it for the product. Meta still has to approve it — submit it in
            the Meta console, then sync to pull the status back.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                placeholder="renewal_reminder"
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Lower case and underscores — Meta&rsquo;s rule, applied on save.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Input
                value={form.language}
                onChange={(event) => setForm({ ...form, language: event.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select
              value={form.category}
              onChange={(event) => setForm({ ...form, category: event.target.value })}
            >
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
              <option value="AUTHENTICATION">Authentication</option>
            </Select>
            <p className="text-xs text-muted-foreground">
              Meta bills these differently — utility is cheaper than marketing.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Body</Label>
            <Textarea
              rows={4}
              value={form.bodyText}
              placeholder="Hello {{1}}, your contract ends on {{2}}."
              onChange={(event) => setForm({ ...form, bodyText: event.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Placeholders are numbered <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code> — that is
              Meta&rsquo;s format, not ours.
            </p>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!accountId || save.isPending || form.name.length < 2 || !form.bodyText}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({ accountId, ...form });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                );
              }
            }}
          >
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Numbers() {
  const { data: accounts = [], isLoading } = useWhatsappAccounts();
  const [adding, setAdding] = useState(false);

  if (isLoading) return <TableSkeleton columns={2} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="marketing.campaign.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Connect a number
          </Button>
        </Can>
      </div>

      {accounts.map((account) => (
        <Card key={account.id}>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-medium">{account.label}</h3>
                <p className="text-sm text-muted-foreground">{account.displayNumber}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {account.templateCount} templates · {account.conversationCount} conversations
                </p>
              </div>
              <Badge variant={account.isActive ? 'success' : 'outline'}>
                {account.isActive ? 'active' : 'off'}
              </Badge>
            </div>

            <NumberSalesbot account={account} />

            {account.lastError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                Last error from Meta: {account.lastError}
              </p>
            )}

            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="text-xs font-medium">Webhook URL for the Meta console</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all text-[11px]">{account.webhookUrl}</code>
                <button
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => void navigator.clipboard.writeText(account.webhookUrl)}
                  aria-label="Copy"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {adding && <AccountDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

/** Which Salesbot answers new WhatsApp conversations on this number. */
function NumberSalesbot({ account }: { account: WhatsappAccount }) {
  const save = useSaveWhatsappAccount();
  const { data: bots = [] } = useSalesbots();
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Salesbot for new chats:</span>
      <Select
        value={account.salesbotId ?? ''}
        disabled={save.isPending}
        onChange={(event) =>
          save.mutate({
            id: account.id,
            label: account.label,
            phoneNumberId: account.phoneNumberId,
            wabaId: account.wabaId,
            displayNumber: account.displayNumber,
            isActive: account.isActive,
            salesbotId: event.target.value || null,
            replyWithinMinutes: account.replyWithinMinutes,
          })
        }
        className="h-8 w-auto text-xs"
      >
        <option value="">None</option>
        {bots.map((bot) => (
          <option key={bot.id} value={bot.id}>
            {bot.name}
            {bot.status !== 'ACTIVE' ? ' (off)' : ''}
          </option>
        ))}
      </Select>
      <span className="ms-2 text-muted-foreground">Reply within:</span>
      <Select
        value={account.replyWithinMinutes ? String(account.replyWithinMinutes) : ''}
        disabled={save.isPending}
        onChange={(event) =>
          save.mutate({
            id: account.id,
            label: account.label,
            phoneNumberId: account.phoneNumberId,
            wabaId: account.wabaId,
            displayNumber: account.displayNumber,
            isActive: account.isActive,
            salesbotId: account.salesbotId,
            replyWithinMinutes: event.target.value ? Number(event.target.value) : null,
          })
        }
        className="h-8 w-auto text-xs"
      >
        <option value="">No target</option>
        {REPLY_TARGETS.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
    </div>
  );
}

function AccountDialog({ onClose }: { onClose: () => void }) {
  const save = useSaveWhatsappAccount();
  const [form, setForm] = useState({
    label: '',
    phoneNumberId: '',
    wabaId: '',
    displayNumber: '',
    accessToken: '',
  });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a WhatsApp number</DialogTitle>
          <DialogDescription>
            From your Meta app: the phone number ID, the WhatsApp Business Account ID and a
            permanent access token. The token is encrypted before it is stored and never returned
            to the browser.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input
                value={form.label}
                placeholder="Support line"
                onChange={(event) => setForm({ ...form, label: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Number</Label>
              <Input
                value={form.displayNumber}
                placeholder="+971 50 123 4567"
                onChange={(event) => setForm({ ...form, displayNumber: event.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Phone number ID</Label>
            <Input
              value={form.phoneNumberId}
              onChange={(event) => setForm({ ...form, phoneNumberId: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>WABA ID</Label>
            <Input
              value={form.wabaId}
              onChange={(event) => setForm({ ...form, wabaId: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Access token</Label>
            <Input
              type="password"
              value={form.accessToken}
              onChange={(event) => setForm({ ...form, accessToken: event.target.value })}
            />
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              save.isPending ||
              !form.label ||
              !form.phoneNumberId ||
              !form.wabaId ||
              !form.accessToken
            }
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync(form);
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not connect it.',
                );
              }
            }}
          >
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
